import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { encryptQRData, generateTestEncryptedQR } from '../../utils/qrEncryption';
import { addNotification } from '../../store/slices/uiSlice';

const QRTestHelper = () => {
  const dispatch = useDispatch();
  const [testData, setTestData] = useState({
    worship_id: '123456',
    mobile: '1234567890',
    current_datetime: new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '').slice(0, 12)
  });
  const [qrFormat, setQrFormat] = useState('encrypted'); // 'json' or 'encrypted'

  const generateQRData = () => {
    if (qrFormat === 'encrypted') {
      try {
        return encryptQRData(testData);
      } catch (error) {
        console.error('Encryption error:', error);
        return 'Encryption Error: ' + error.message;
      }
    } else {
      return JSON.stringify(testData);
    }
  };

  const updateDateTime = () => {
    setTestData(prev => ({
      ...prev,
      current_datetime: new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '').slice(0, 12)
    }));
  };

  const qrDataString = generateQRData();

  return (
    <div className="bg-blue-50 p-4 rounded-lg mb-4">
      <h3 className="text-lg font-semibold mb-3 text-blue-800">QR Test Helper</h3>
      
      {/* Test Data Configuration */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Worship ID:
          </label>
          <input
            type="text"
            value={testData.worship_id}
            onChange={(e) => setTestData(prev => ({ ...prev, worship_id: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Mobile:
          </label>
          <input
            type="text"
            value={testData.mobile}
            onChange={(e) => setTestData(prev => ({ ...prev, mobile: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Current DateTime:
          </label>
          <div className="flex">
            <input
              type="text"
              value={testData.current_datetime}
              onChange={(e) => setTestData(prev => ({ ...prev, current_datetime: e.target.value }))}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="YYYYMMDDHHMM"
            />
            <button
              onClick={updateDateTime}
              className="px-3 py-2 bg-blue-500 text-white rounded-r-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Now
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            QR Format:
          </label>
          <select
            value={qrFormat}
            onChange={(e) => setQrFormat(e.target.value)}
            className="w-3/4 px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="encrypted">🔐 Encrypted (Secure)</option>
            <option value="json">📄 JSON (Legacy)</option>
          </select>
        </div>
      </div>

      {/* Generated QR Data */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <label className="block text-sm font-medium text-gray-700">
            Generated QR Data:
          </label>
          <span className="text-xs text-gray-500">
            {qrFormat === 'encrypted' ? '🔐 AES Encrypted' : '📄 Plain JSON'}
          </span>
        </div>
        <div className="bg-white p-3 rounded border border-gray-300 font-mono text-sm break-all">
          {qrDataString}
        </div>
        {qrFormat === 'encrypted' && (
          <p className="text-xs text-gray-600 mt-1">
            ✅ This encrypted format prevents data leakage from QR codes
          </p>
        )}
      </div>

      {/* QR Code Display (using a simple text representation) */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          QR Code Preview (for testing):
        </label>
        <div className="bg-white p-4 rounded border border-gray-300 text-center">
          <div className="inline-block bg-black text-white p-8 text-xs font-mono leading-tight">
            QR CODE<br/>
            {testData.worship_id}<br/>
            {testData.current_datetime}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            This is a visual representation. Use an actual QR generator for real codes.
          </p>
        </div>
      </div>

      {/* Copy to Clipboard */}
      <div className="flex gap-2">
        <button
          onClick={() => {
            navigator.clipboard.writeText(qrDataString).then(() => {
              dispatch(addNotification({
                type: 'success',
                title: 'Success',
                message: 'QR data copied to clipboard!'
              }));
            });
          }}
          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded font-medium"
        >
          Copy QR Data
        </button>
        <button
          onClick={() => {
            // Generate a QR code URL for testing (using a free online service)
            const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrDataString)}`;
            window.open(qrUrl, '_blank');
          }}
          className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded font-medium"
        >
          Generate QR Code
        </button>
      </div>

      {/* Expected Format Info */}
      <div className="mt-4 text-xs text-gray-600">
        <strong>Format Notes:</strong>
        <ul className="list-disc list-inside mt-1">
          <li>worship_id: Numeric identifier for the person</li>
          <li>current_datetime: YYYYMMDDHHMM format (e.g., 202508171354 for Aug 17, 2025 13:54)</li>
          <li>Must be valid JSON format</li>
        </ul>
      </div>
    </div>
  );
};

export default QRTestHelper;
